import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CookiePolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-green-600 hover:text-green-700 mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Torna alla home
        </button>

        <h1 className="text-4xl font-bold mb-8">Cookie Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT')}</p>

        <div className="prose prose-lg max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-bold mb-4">1. Cosa sono i Cookie</h2>
            <p>
              I cookie sono piccoli file di testo che vengono memorizzati sul tuo dispositivo quando visiti un sito web. 
              Vengono utilizzati per far funzionare il sito in modo efficiente, migliorare l'esperienza dell'utente e fornire 
              informazioni ai proprietari del sito.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">2. Tipologie di Cookie Utilizzati</h2>
            
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3">🔧 Cookie Tecnici (Necessari)</h3>
                <p className="mb-3">
                  Essenziali per il corretto funzionamento del sito. Senza questi cookie, alcune funzionalità del sito non funzionerebbero.
                </p>
                <table className="w-full text-sm">
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="p-2 text-left">Nome</th>
                      <th className="p-2 text-left">Scopo</th>
                      <th className="p-2 text-left">Durata</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="p-2">cookieConsent</td>
                      <td className="p-2">Memorizza le tue preferenze sui cookie</td>
                      <td className="p-2">12 mesi</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-2">sessionId</td>
                      <td className="p-2">Mantiene la sessione attiva</td>
                      <td className="p-2">Sessione</td>
                    </tr>
                  </tbody>
                </table>
                <p className="mt-3 text-sm text-gray-600">
                  <strong>Base giuridica:</strong> Legittimo interesse del titolare (non richiedono consenso)
                </p>
              </div>

              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
                <h3 className="text-xl font-bold mb-3">📊 Cookie Analitici</h3>
                <p className="mb-3">
                  Ci aiutano a capire come i visitatori interagiscono con il sito raccogliendo informazioni in forma anonima.
                </p>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold mb-2">Google Analytics</h4>
                    <table className="w-full text-sm">
                      <thead className="bg-blue-100">
                        <tr>
                          <th className="p-2 text-left">Nome</th>
                          <th className="p-2 text-left">Scopo</th>
                          <th className="p-2 text-left">Durata</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="p-2">_ga</td>
                          <td className="p-2">Distingue gli utenti</td>
                          <td className="p-2">2 anni</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-2">_ga_*</td>
                          <td className="p-2">Mantiene lo stato della sessione</td>
                          <td className="p-2">2 anni</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-2">_gid</td>
                          <td className="p-2">Distingue gli utenti</td>
                          <td className="p-2">24 ore</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <p className="mt-3 text-sm text-gray-600">
                  <strong>Base giuridica:</strong> Consenso dell'utente (Art. 122 Codice Privacy)<br />
                  <strong>Fornitore:</strong> Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA<br />
                  <strong>Privacy Policy:</strong>{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                    policies.google.com/privacy
                  </a>
                </p>
              </div>

              <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
                <h3 className="text-xl font-bold mb-3">🎯 Cookie di Marketing</h3>
                <p className="mb-3">
                  Utilizzati per tracciare i visitatori attraverso i siti web e mostrare annunci pertinenti.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold mb-2">Google Ads</h4>
                    <table className="w-full text-sm">
                      <thead className="bg-green-100">
                        <tr>
                          <th className="p-2 text-left">Nome</th>
                          <th className="p-2 text-left">Scopo</th>
                          <th className="p-2 text-left">Durata</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="p-2">_gcl_au</td>
                          <td className="p-2">Traccia le conversioni</td>
                          <td className="p-2">90 giorni</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-2">test_cookie</td>
                          <td className="p-2">Verifica supporto cookie</td>
                          <td className="p-2">15 minuti</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div>
                    <h4 className="font-bold mb-2">Meta Pixel (Facebook/Instagram)</h4>
                    <table className="w-full text-sm">
                      <thead className="bg-green-100">
                        <tr>
                          <th className="p-2 text-left">Nome</th>
                          <th className="p-2 text-left">Scopo</th>
                          <th className="p-2 text-left">Durata</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="p-2">_fbp</td>
                          <td className="p-2">Traccia visite e conversioni</td>
                          <td className="p-2">90 giorni</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-2">fr</td>
                          <td className="p-2">Pubblicità personalizzata</td>
                          <td className="p-2">90 giorni</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <p className="mt-3 text-sm text-gray-600">
                  <strong>Base giuridica:</strong> Consenso dell'utente (Art. 122 Codice Privacy)<br />
                  <strong>Fornitori:</strong><br />
                  - Google LLC: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">policies.google.com/privacy</a><br />
                  - Meta Platforms Ireland Limited: <a href="https://www.facebook.com/privacy" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">facebook.com/privacy</a>
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">3. Come Gestire i Cookie</h2>
            <p className="mb-4">
              Puoi gestire le tue preferenze sui cookie in qualsiasi momento tramite:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Banner cookie:</strong> Apparirà alla tua prima visita, dove potrai scegliere quali cookie accettare</li>
              <li><strong>Impostazioni browser:</strong> Tutti i browser permettono di bloccare o eliminare i cookie. Consulta la guida del tuo browser</li>
              <li><strong>Strumenti di opt-out:</strong>
                <ul className="list-circle pl-6 mt-2 space-y-1">
                  <li>Google Ads: <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">adssettings.google.com</a></li>
                  <li>Facebook: <a href="https://www.facebook.com/settings?tab=ads" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">facebook.com/settings?tab=ads</a></li>
                </ul>
              </li>
            </ul>
            <p className="mt-4 bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
              ⚠️ <strong>Nota:</strong> Disabilitando i cookie necessari, alcune funzionalità del sito potrebbero non funzionare correttamente.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">4. Cookie di Terze Parti</h2>
            <p>
              Il nostro sito utilizza cookie di terze parti (Google, Meta) per fornire servizi di analytics e pubblicità. 
              Questi fornitori potrebbero utilizzare i dati raccolti anche per i propri scopi. Per maggiori informazioni, 
              consulta le loro privacy policy:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>
                <strong>Google:</strong>{' '}
                <a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">
                  policies.google.com/technologies/cookies
                </a>
              </li>
              <li>
                <strong>Meta:</strong>{' '}
                <a href="https://www.facebook.com/policies/cookies" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">
                  facebook.com/policies/cookies
                </a>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">5. Aggiornamenti</h2>
            <p>
              Questa Cookie Policy può essere aggiornata periodicamente. Ti invitiamo a consultare questa pagina regolarmente. 
              La data dell'ultimo aggiornamento è indicata in cima alla pagina.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">6. Contatti</h2>
            <p>Per domande riguardo questa Cookie Policy, contattaci:</p>
            <div className="bg-gray-50 p-6 rounded-lg mt-4">
              <p><strong>Revogreen Srl</strong></p>
              <p>Via Roma 44B, Mozzecane (VR), Italia</p>
              <p>Email: <a href="mailto:info@revogreenitalia.com" className="text-green-600 underline">info@revogreenitalia.com</a></p>
              <p>Privacy: <a href="mailto:privacy@revogreenitalia.com" className="text-green-600 underline">privacy@revogreenitalia.com</a></p>
              <p>Telefono: 800 86 46 21</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
